import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import Lenis from "lenis";

const SmoothScroll = ({ children }: { children: React.ReactNode }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001,
    });

    /* ── Noise distortion on scroll (velocity‑based) ──────────── */
    const filterContainerRef = useRef<HTMLDivElement>(null);
    const turbulenceRef = useRef<SVGFETurbulenceElement>(null);

    useEffect(() => {
        const lenis = new Lenis({
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            orientation: "vertical",
            gestureOrientation: "vertical",
            smoothWheel: true,
            wheelMultiplier: 1,
            touchMultiplier: 2,
            infinite: false,
        });

        // Expose lenis to window for use in other components
        (window as any).lenis = lenis;

        function raf(time: number) {
            lenis.raf(time);

            // Calculate scroll velocity for noise distortion without triggering React re-renders!
            const velocity = Math.abs(lenis.velocity || 0);
            const t = Math.min(velocity / 800, 0.012); // small subtle value

            if (turbulenceRef.current) {
                turbulenceRef.current.setAttribute("baseFrequency", String(t));
            }
            if (filterContainerRef.current) {
                filterContainerRef.current.style.filter = t > 0.001 ? "url(#noise-distortion)" : "none";
            }

            requestAnimationFrame(raf);
        }

        requestAnimationFrame(raf);

        const onResize = () => {
            lenis.resize();
        };
        window.addEventListener("resize", onResize);

        return () => {
            lenis.destroy();
            window.removeEventListener("resize", onResize);
            delete (window as any).lenis;
        };
    }, []);

    return (
        <div ref={scrollRef}>
            {/* SVG noise distortion filter */}
            <svg className="fixed pointer-events-none" width="0" height="0" aria-hidden="true" style={{ position: 'absolute' }}>
                <defs>
                    <filter id="noise-distortion">
                        <feTurbulence
                            ref={turbulenceRef}
                            type="fractalNoise"
                            baseFrequency="0"
                            numOctaves="3"
                            result="noise"
                        />
                        <feDisplacementMap
                            in="SourceGraphic"
                            in2="noise"
                            scale="3"
                            xChannelSelector="R"
                            yChannelSelector="G"
                        />
                    </filter>
                </defs>
            </svg>

            {/* Scroll progress bar */}
            <motion.div
                className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-pulse to-electric z-[100] origin-left"
                style={{ scaleX }}
            />

            {/* Apply subtle noise distortion to content during fast scroll */}
            <div
                ref={filterContainerRef}
                style={{ transition: "filter 0.2s ease-out" }}
            >
                {children}
            </div>
        </div>
    );
};

export default SmoothScroll;
